import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { FC, ReactNode, useMemo, useState } from "react";
import { format } from "date-fns";
import { RewardStudent } from "../../../types/slices/rewardStudentType";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CountUp from "react-countup";

const RewardCardType = ({
  title,
  items,
  variant,
}: {
  title: string;
  items: (string | ReactNode)[];
  variant?: string;
}) => {
  const itemsList = items.map((item, idx) => (
    <li key={`${idx}-item`}>
      <Typography variant="body2" component="div">
        {item}
      </Typography>
    </li>
  ));

  const ItemsView = (() => {
    if (variant === "ol") {
      return <ol>{itemsList}</ol>;
    }

    return <ul>{itemsList}</ul>;
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 2, sm: 2.5 },
        border: "1px solid rgba(1,77,223,0.14)",
        borderTop: "4px solid #014DDF",
        borderRadius: 3,
        bgcolor: "rgba(255,255,255,0.92)",
        boxShadow: "0 12px 30px rgba(10,34,156,0.07)",
        "& ul, & ol": { pl: 2.25, mb: 0 },
        "& li": { mb: 1 },
        "& li::marker": { color: "#014DDF", fontWeight: 700 },
      }}
    >
      {title && (
        <Typography variant="subtitle1" component="h6" sx={{ fontWeight: 700, color: "#0A229C" }}>
          {title}
        </Typography>
      )}
      {ItemsView}
    </Paper>
  );
};

const RewardsDetail: FC<{
  onclick: () => void;
  points: number;
  wonPoints: RewardStudent[];
  exchangedPoints: RewardStudent[];
}> = ({ onclick, points, wonPoints, exchangedPoints }) => {
  const [showAllPoints, setShowAllPoints] = useState<boolean>(false);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setShowAllPoints(open);
    };

  const hadPoints = useMemo(
    () => wonPoints.length > 0 && points === 0,
    [wonPoints, points]
  );

  return (
    <>
      <Box
        sx={{
          textAlign: "left",
          width: "100%",
          maxWidth: 980,
          marginBottom: 2,
        }}
      >
        <Button
          type="button"
          onClick={onclick}
          color="primary"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ minHeight: 44, fontWeight: 700 }}
        >
          Mis certificados
        </Button>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 980,
        }}
      >
        <Paper
          className="focus-result-hero"
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: { xs: 2.5, sm: 3.5 },
            color: "common.white",
            borderRadius: 4,
            background: "linear-gradient(125deg, #0A229C 0%, #014DDF 58%, #4100DE 100%)",
            boxShadow: "0 18px 42px rgba(10,34,156,0.2)",
            "&::after": {
              content: '""',
              position: "absolute",
              width: 180,
              height: 180,
              top: -105,
              right: -30,
              borderRadius: "50%",
              border: "34px solid rgba(0,217,255,0.18)",
            },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Box
              aria-hidden="true"
              sx={{ width: 58, height: 58, display: "grid", placeItems: "center", flexShrink: 0, color: "#FDCB6E", bgcolor: "rgba(255,255,255,0.14)", borderRadius: 3 }}
            >
              <EmojiEventsRoundedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ opacity: 0.76, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Mis puntos Focus
              </Typography>
              <Typography
                variant="h4"
                component="h4"
                sx={{ mt: 0.25, fontSize: { xs: 30, sm: 38 }, fontWeight: 700, color: "common.white" }}
              >
                <CountUp end={points} /> puntos
              </Typography>
              <Box sx={{ mt: 0.5, color: "rgba(255,255,255,0.82)", fontSize: 13 }}>
                {Boolean(points) && (
                  <>Vigentes hasta 31/12/{new Date().getFullYear()}</>
                )}
                {points === 0 && (
                  <>
                    {hadPoints ? (
                      <Stack direction="row" gap={1}>
                        <CelebrationOutlinedIcon sx={{ color: "orange" }} />
                        <>
                          Canjeaste todos tus puntos. ¡Sigue acumulando!
                        </>
                      </Stack>
                    ) : (
                      <Stack direction="row" gap={1}>
                        <InfoOutlinedIcon sx={{ color: "blue" }} />
                        <>
                          Descubre cómo acumular y canjear puntos.
                        </>
                      </Stack>
                    )}
                  </>
                )}
              </Box>
              {wonPoints.length > 0 && (
                <Button
                  onClick={toggleDrawer(true)}
                  sx={{ mt: 1, minHeight: 44, px: 0, color: "#00D9FF", fontWeight: 700 }}
                >
                  Ver movimientos
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 980,
          marginTop: 3,
        }}
      >
        <Typography variant="h5" sx={{ mb: 1.5, color: "primary.dark", fontWeight: 700 }}>
          Cómo usar mis puntos
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" }, gap: 1.5 }}>
        <RewardCardType
          title="Acumular"
          items={[
            <>
              <strong>30 puntos</strong> acumulados de{" "}
              <strong>01 Constancia Digital</strong>
            </>,
            <>
              <strong>50 puntos</strong> acumulados de{" "}
              <strong>01 Constancia Física</strong>
            </>,
            <>
              <strong>80 puntos</strong> acumulados de{" "}
              <strong>01 Certificado Digital</strong>
            </>,
            <>
              <strong>100 puntos</strong> acumulados de{" "}
              <strong>01 Certificado Físico</strong>
            </>,
          ]}
        />
        <RewardCardType
          title="Convertir en soles"
          items={[
            <>
              <strong>500 puntos</strong>, obtienes <strong>S/ 5.00</strong>
            </>,
            <>
              <strong>800 puntos</strong>, obtienes <strong>S/ 10.00</strong>
            </>,
            <>
              <strong>1000 puntos</strong>, obtienes <strong>S/ 15.00</strong>
            </>,
            <>
              <strong>1200 puntos</strong>, obtienes <strong>S/ 20.00</strong>
            </>,
            <>
              Puedes usarlo para descuentos en{" "}
              <strong>certificaciones, constancias, envíos</strong> ó recibir
              dinero directo a tu <strong>Yape ó Plin</strong>
            </>,
          ]}
        />
        <RewardCardType
          title="Canjear documentos"
          items={[
            <>
              <strong>440 puntos</strong>, obtienes{" "}
              <strong>01 Constancia Digital</strong>
            </>,
            <>
              <strong>680 puntos</strong>, obtienes{" "}
              <strong>01 Certificado Digital</strong>
            </>,
            <>
              <strong>780 puntos</strong>, obtienes{" "}
              <strong>01 Certificado Físico + Digital</strong>
            </>,
            <>
              <strong>950 puntos</strong>, obtienes{" "}
              <strong>
                01 Certificado Físico + Digital + Constancia Digital
              </strong>
            </>,
            <>
              <strong>1150 puntos</strong>, obtienes{" "}
              <strong>
                01 Certificado Físico + Digital + Constancia Física
              </strong>
            </>,
          ]}
        />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 980,
          marginTop: 3,
        }}
      >
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid rgba(1,77,223,0.14)",
            borderRadius: "16px !important",
            overflow: "hidden",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            sx={{ minHeight: 56, px: 2.5, "& .MuiAccordionSummary-content": { my: 1.5 } }}
          >
            <Typography sx={{ color: "primary.dark", fontWeight: 700 }}>
              Términos y condiciones
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
        <RewardCardType
          title=""
          variant="ol"
          items={[
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Acumulación de Puntos:
              </Typography>{" "}
              Los puntos se otorgan automáticamente en la plataforma por la
              adquisición de constancias y certificados, siempre y cuando el
              estudiante apruebe o cumpla con el reglamento establecido.
              <br />
              <br />
            </>,
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Canje Mínimo:
              </Typography>{" "}
              El canje de dinero solo es posible a partir de{" "}
              <strong>500 puntos (S/5.00)</strong> y se realizará a través de{" "}
              <strong>Yape o Plin.</strong>
              <br />
              <br />
            </>,
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Validez de Puntos:
              </Typography>{" "}
              Los puntos acumulados se mantendrán vigentes hasta que sean
              canjeados por algún premio. Una vez canjeados, el saldo se
              reiniciará automáticamente y continuará acumulándose hasta
              alcanzar la cantidad necesaria para un nuevo canje.
              <br />
              <br />
            </>,
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Uso de Beneficios:
              </Typography>{" "}
              Los puntos solo pueden ser canjeados por{" "}
              <strong>certificaciones, constancias o dinero</strong>, siempre
              que el estudiante apruebe o cumpla con el reglamento establecido.
              Los puntos no son transferibles a otra persona que estudie o haya
              estudiado en nuestra institución.
              <br />
              <br />
            </>,
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Modificaciones:
              </Typography>{" "}
              La institución se reserva el derecho de modificar los términos del
              programa en cualquier momento, informando previamente a los
              participantes.
              <br />
              <br />
            </>,
            <>
              <Typography
                variant="subtitle2"
                style={{ textDecoration: "underline", fontWeight: 600 }}
              >
                Nota importante:
              </Typography>{" "}
              Los puntos se acumulan anualmente y se reinician a cero al
              comenzar cada nuevo año. El sistema de puntos será válido a partir
              del <strong>19 de marzo del 2025</strong>.
            </>,
          ]}
        />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Drawer anchor="right" open={showAllPoints} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: { xs: "100vw", sm: 500 },
            maxWidth: "100vw",
            minHeight: "100%",
            p: { xs: 2.5, sm: 4 },
            bgcolor: "#F7F9FC",
          }}
          role="dialog"
          aria-label="Mis movimientos de puntos"
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box>
              <Typography component="h2" variant="h5" sx={{ color: "primary.dark", fontWeight: 700 }}>
                Mis movimientos
              </Typography>
              <Typography variant="body2" color="text.secondary">Historial de puntos Focus</Typography>
            </Box>
            <IconButton onClick={toggleDrawer(false)} aria-label="Cerrar movimientos" sx={{ width: 44, height: 44 }}>
              <CancelOutlinedIcon />
            </IconButton>
          </Stack>
          <Typography component="h3" sx={{ mb: 1.5, color: "primary.dark", fontWeight: 700 }}>
            Acumulados
          </Typography>
          <Box sx={{ width: "100%", color: "gray" }}>
            <Grid
              container
              spacing={{ xs: 1, md: 3 }}
              columns={{ xs: 6, sm: 8, md: 12 }}
            >
              <Grid item xs={2} sm={4} md={4}>
                Emisión
              </Grid>
              <Grid item xs={3} sm={5} md={5}>
                Tipo
              </Grid>
              <Grid item xs={1} sm={3} md={3}>
                Puntos
              </Grid>
            </Grid>
            {wonPoints.map(({ issueDate, concept, points }, index) => (
              <div key={`grid-${index}`}>
                <Divider style={{ borderStyle: "dashed", margin: "10px 0" }} />
                <Grid
                  container
                  spacing={{ xs: 2, md: 3 }}
                  columns={{ xs: 6, sm: 8, md: 12 }}
                >
                  <Grid item xs={2} sm={4} md={4}>
                    {format(issueDate, "dd/MM/yyyy")}
                  </Grid>
                  <Grid item xs={3} sm={5} md={5}>
                    {concept}
                  </Grid>
                  <Grid item xs={1} sm={3} md={3}>
                    {points}
                  </Grid>
                </Grid>
              </div>
            ))}
          </Box>
          <br />
          <br />
          {!!exchangedPoints.length && (
            <>
              <Typography component="h5" variant="h6">
                Canjeados
              </Typography>
              <br />
              <Box sx={{ width: "100%", color: "gray" }}>
                <Grid
                  container
                  spacing={{ xs: 1, md: 3 }}
                  columns={{ xs: 6, sm: 8, md: 12 }}
                >
                  <Grid item xs={2} sm={4} md={4}>
                    Emisión
                  </Grid>
                  <Grid item xs={3} sm={5} md={5}></Grid>
                  <Grid item xs={1} sm={3} md={3}>
                    Puntos
                  </Grid>
                </Grid>
                {exchangedPoints.map(({ issueDate, points }, index) => (
                  <div key={`grid-${index}`}>
                    <Divider
                      style={{ borderStyle: "dashed", margin: "10px 0" }}
                    />
                    <Grid
                      container
                      spacing={{ xs: 2, md: 3 }}
                      columns={{ xs: 6, sm: 8, md: 12 }}
                    >
                      <Grid item xs={2} sm={4} md={4}>
                        {format(issueDate, "dd/MM/yyyy")}
                      </Grid>
                      <Grid item xs={3} sm={5} md={5}></Grid>
                      <Grid item xs={1} sm={3} md={3}>
                        {points}
                      </Grid>
                    </Grid>
                  </div>
                ))}
              </Box>
              <br />
              <br />
            </>
          )}
          <div
            style={{
              backgroundColor: "#f5f9fa",
              padding: 18,
              borderRadius: 20,
            }}
          >
            <div
              style={{
                float: "left",
                height: 150,
                marginRight: 18,
                paddingTop: 10,
              }}
            >
              <img
                alt="constancia"
                src="./icons/hand-money-icon.svg"
                width={60}
              />
            </div>
            <Typography
              variant="h4"
              component="h4"
              style={{ fontSize: 16, fontWeight: 600, color: "#133fe7" }}
            >
              ¡Canjea tus puntos hoy!
            </Typography>
            <div style={{ fontSize: 14, marginTop: 5 }}>
              Sigue capacitándote en Focus para sumar nuevos beneficios.
            </div>
          </div>
        </Box>
      </Drawer>
    </>
  );
};

export default RewardsDetail;
